<script setup lang="ts">
import freeGiftRibbonImage from '@/images/free_gift_ribbon.png';
import type { GiftProduct } from '@/shared/composables/cart/useGiftProducts';
import { useTranslations } from '@/shared/composables/useTranslations';
import { formatPriceStorefront } from '@/shared/utils/shopify/money-formatter';

const props = withDefaults(
  defineProps<{
    product: GiftProduct;
    index: number;
    isDisabled?: boolean;
  }>(),
  {
    isDisabled: false
  }
);

const emit = defineEmits<{
  addToCart: [
    variantId: number,
    productId: string,
    productTitle: string,
    index: number
  ];
}>();

const { t } = useTranslations();

const getImageUrl = (url: string, width: number): string => {
  // Shopify CDN URL handling - if it's already a Shopify CDN URL, modify it
  if (url.includes('cdn.shopify.com')) {
    // Remove existing size parameters and add new width
    const baseUrl = url.split('?')[0];
    return `${baseUrl}?width=${width}`;
  }
  return url;
};

const handleClick = () => {
  emit(
    'addToCart',
    props.product.variantId,
    props.product.id,
    props.product.title,
    props.index
  );
};
</script>

<template>
  <div
    class="js-reward-product-slide flex w-full select-none items-center justify-center rounded-md border border-primary-dark-100 bg-primary-25 p-2 text-sm"
    :data-product-id="product.id"
  >
    <div class="flex w-full items-center justify-between gap-3">
      <!-- Col 1: Product Image and Details -->
      <div class="flex min-w-0 flex-1 items-start gap-2">
        <div class="relative size-20 shrink-0">
          <div class="size-full overflow-hidden rounded-md bg-white">
            <img
              :src="getImageUrl(product.imageUrl, 150)"
              width="150"
              height="150"
              :alt="product.imageAlt"
              class="size-full object-cover"
              loading="lazy"
            />
          </div>

          <div class="absolute left-0 top-0 size-12">
            <img
              :src="freeGiftRibbonImage"
              alt="Free gift"
              width="100"
              height="100"
              class="size-full object-contain"
              loading="lazy"
            />
          </div>
        </div>

        <!-- Product Details -->
        <div class="my-auto min-w-0 flex-1">
          <h4 class="mb-1 line-clamp-2 text-xs font-bold text-gray-darkest">
            {{ product.title }}
          </h4>

          <div class="flex items-center gap-1">
            <span class="text-xs text-gray-500 line-through">
              {{ formatPriceStorefront(product.price) }}
            </span>
            <span class="text-xs font-semibold text-primary">
              {{ t('free_gift.free') }}
            </span>
          </div>
        </div>
      </div>

      <!-- Col 2: Add Button -->
      <div class="shrink-0">
        <button
          type="button"
          class="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-bold text-white no-underline transition-colors hover:bg-primary-dark hover:no-underline focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isDisabled"
          @click="handleClick"
        >
          {{ t('free_gift.add') }}
        </button>
      </div>
    </div>
  </div>
</template>
